/**
 * Enhanced Cache Manager for improved application performance
 * Provides caching for API requests, calculations, and other data
 * Includes runtime optimization based on network conditions and device capabilities
 */

class CacheManager {
  constructor() {
    this.cache = new Map();
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes default TTL
    this.pendingRequests = new Map(); // For request deduplication
    this.networkCondition = 'unknown';
    this.deviceCapability = 'unknown';
    
    // Initialize runtime optimization features
    this.initializeRuntimeOptimization();
  }

  /**
   * Set a value in the cache with optional TTL
   * @param {string} key - Cache key
   * @param {any} data - Data to cache
   * @param {number} ttl - Time to live in ms (optional)
   */
  set(key, data, ttl = this.defaultTTL) {
    this.cache.set(key, {
      data,
      expiry: Date.now() + ttl
    });
  }

  /**
   * Get a value from the cache
   * @param {string} key - Cache key
   * @returns {any|null} - Cached data or null if not found/expired
   */
  get(key) {
    const entry = this.cache.get(key);
    if (!entry) return null;

    if (Date.now() > entry.expiry) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  /**
   * Check if a key exists in cache and is valid
   * @param {string} key - Cache key
   * @returns {boolean} - Whether the key exists and is valid
   */
  has(key) {
    return this.get(key) !== null;
  }
  
  /**
   * Clear all cached data
   */
  clearAll() {
    this.cache.clear();
    console.log('Cache cleared');
  }

  /**
   * Delete a specific entry from the cache
   * @param {string} key - Cache key
   */
  delete(key) {
    this.cache.delete(key);
  }

  /**
   * Clear all entries from the cache
   */
  clear() {
    this.cache.clear();
  }

  /**
   * Clear expired entries from the cache
   * @returns {number} - Number of entries cleared
   */
  clearExpired() {
    let cleared = 0;
    const now = Date.now();
    
    for (const [key, entry] of this.cache.entries()) {
      if (now > entry.expiry) {
        this.cache.delete(key);
        cleared++;
      }
    }
    
    return cleared;
  }
  
  /**
   * Initialize runtime optimization features
   * - Monitors network conditions
   * - Detects device capabilities
   * - Adjusts caching strategy based on runtime conditions
   */
  initializeRuntimeOptimization() {
    if (typeof window === 'undefined') return;
    
    // Monitor network conditions
    this.monitorNetworkConditions();
    
    // Detect device capabilities
    this.detectDeviceCapabilities();
    
    // Set up periodic optimization checks
    this.setupPeriodicOptimization();
    
    // Log initialization in production only
    if (process.env.NODE_ENV === 'production') {
      console.log('📱 Runtime optimization initialized');
    }
  }
  
  /**
   * Monitor network conditions using Network Information API
   */
  monitorNetworkConditions() {
    const connection = navigator.connection || 
                      navigator.mozConnection || 
                      navigator.webkitConnection;
    
    if (connection) {
      // Store initial network condition
      this.updateNetworkCondition(connection);
      
      // Listen for network changes
      connection.addEventListener('change', () => {
        this.updateNetworkCondition(connection);
        this.adjustCachingStrategy();
      });
    } else {
      // Fallback for browsers without Network Information API
      window.addEventListener('online', () => {
        this.networkCondition = 'online';
        this.adjustCachingStrategy();
      });
      
      window.addEventListener('offline', () => {
        this.networkCondition = 'offline';
        this.adjustCachingStrategy();
      });
    }
  }
  
  /**
   * Update stored network condition based on connection info
   * @param {NetworkInformation} connection - Network connection info
   */
  updateNetworkCondition(connection) {
    // Store basic network information
    this.networkCondition = {
      type: connection.type || 'unknown',
      effectiveType: connection.effectiveType || 'unknown',
      downlink: connection.downlink || 0,
      rtt: connection.rtt || 0,
      saveData: !!connection.saveData
    };
    
    // Log network change in development mode
    if (process.env.NODE_ENV !== 'production') {
      console.log('🌐 Network condition:', this.networkCondition);
    }
  }
  
  /**
   * Detect device capabilities (memory, CPU cores, etc.)
   */
  detectDeviceCapabilities() {
    const memory = navigator.deviceMemory;
    const hardwareConcurrency = navigator.hardwareConcurrency;
    const isLowEndDevice = memory <= 2 || hardwareConcurrency <= 2;
    
    this.deviceCapability = {
      memory: memory || 'unknown',
      cores: hardwareConcurrency || 'unknown',
      isLowEndDevice: isLowEndDevice
    };
    
    // Log device capabilities in development mode
    if (process.env.NODE_ENV !== 'production') {
      console.log('💻 Device capabilities:', this.deviceCapability);
    }
  }
  
  /**
   * Set up periodic checks to optimize caching strategy
   */
  setupPeriodicOptimization() {
    // Check less frequently in production to reduce overhead
    const checkInterval = process.env.NODE_ENV === 'production' ? 60000 : 30000;
    
    setInterval(() => {
      this.adjustCachingStrategy();
    }, checkInterval);
  }
  
  /**
   * Adjust caching strategy based on current conditions
   */
  adjustCachingStrategy() {
    // Default TTL (5 minutes)
    let newTTL = 5 * 60 * 1000;
    
    // Production mode uses more aggressive caching
    if (process.env.NODE_ENV === 'production') {
      newTTL = 10 * 60 * 1000; // 10 minutes base in production
    }
    
    // Adjust based on network conditions
    if (this.networkCondition !== 'unknown') {
      // For connection object from Network Information API
      if (typeof this.networkCondition === 'object') {
        // Aggressive caching for slow connections
        if (this.networkCondition.saveData || 
            this.networkCondition.effectiveType === 'slow-2g' || 
            this.networkCondition.effectiveType === '2g') {
          newTTL *= 3; // 3x longer cache for slow connections
        } else if (this.networkCondition.effectiveType === '3g') {
          newTTL *= 2; // 2x longer cache for 3g connections
        }
        
        // Shorter cache for very fast connections
        if (this.networkCondition.downlink > 10 && this.networkCondition.rtt < 50) {
          newTTL *= 0.8; // 20% shorter cache for very fast connections
        }
      } else if (this.networkCondition === 'offline') {
        // Maximum caching when offline
        newTTL = 24 * 60 * 60 * 1000; // 24 hours when offline
      }
    }
    
    // Adjust based on device capabilities
    if (this.deviceCapability.isLowEndDevice) {
      newTTL *= 1.5; // 50% longer cache for low-end devices to reduce processing
    }
    
    // Apply the new TTL
    if (newTTL !== this.defaultTTL) {
      this.defaultTTL = newTTL;
      
      // Log TTL changes in production for significant changes only
      if (process.env.NODE_ENV === 'production') {
        console.log(`📊 Cache TTL adjusted to ${newTTL/1000}s based on runtime conditions`);
      }
    }
  }
  
  /**
   * Get current runtime optimization status
   * @returns {Object} - Current optimization status
   */
  getRuntimeOptimizationStatus() {
    return {
      networkCondition: this.networkCondition,
      deviceCapability: this.deviceCapability,
      currentTTL: this.defaultTTL,
      cacheSize: this.cache.size,
      pendingRequests: this.pendingRequests.size
    };
  }

  /**
   * Deduplicate API requests - returns the same promise for identical in-flight requests
   * @param {string} key - Request identifier
   * @param {Function} requestFn - Function that returns a promise for the request
   * @returns {Promise} - Promise for the request
   */
  async dedupRequest(key, requestFn) {
    // If we already have a request in progress, return that promise
    if (this.pendingRequests.has(key)) {
      console.log(`📤 Request deduplication hit: ${key}`);
      return this.pendingRequests.get(key);
    }

    // Otherwise, start a new request
    const promise = requestFn().finally(() => {
      // Clean up when the request completes
      this.pendingRequests.delete(key);
    });
    
    // Store the request
    this.pendingRequests.set(key, promise);
    return promise;
  }
  
  /**
   * Memoize a function with caching
   * @param {Function} fn - Function to memoize
   * @param {Function} keyFn - Function to generate cache key from arguments
   * @param {number} ttl - Time to live in ms
   * @returns {Function} - Memoized function
   */
  memoize(fn, keyFn = (...args) => JSON.stringify(args), ttl = this.defaultTTL) {
    return (...args) => {
      const key = `memo:${keyFn(...args)}`;
      const cached = this.get(key);
      
      if (cached !== null) {
        return cached;
      }
      
      const result = fn(...args);
      
      // Handle both regular values and promises
      if (result instanceof Promise) {
        return result.then(value => {
          this.set(key, value, ttl);
          return value;
        });
      } else {
        this.set(key, result, ttl);
        return result;
      }
    };
  }
}

export const cacheManager = new CacheManager();

// Export specialized instances for different use cases
export const apiCache = new CacheManager();
apiCache.defaultTTL = 3 * 60 * 1000; // 3 minutes for API responses

export const calculationCache = new CacheManager();
calculationCache.defaultTTL = 10 * 60 * 1000; // 10 minutes for calculations

// Re-export existing cache for backward compatibility
export { jobScoresCache } from './jobScoresCache';